import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './HomePageCMS.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface SubjectOption { value: string; label: string; label_bn: string; }

interface ContactContent {
  hero_title: string;
  hero_title_bn: string;
  hero_subtitle: string;
  hero_subtitle_bn: string;
  location: string;
  location_bn: string;
  email: string;
  business_hours: string;
  business_hours_bn: string;
  form_title: string;
  form_title_bn: string;
  subject_options: SubjectOption[];
}

const EMPTY: ContactContent = {
  hero_title: '', hero_title_bn: '', hero_subtitle: '', hero_subtitle_bn: '',
  location: '', location_bn: '', email: '',
  business_hours: '', business_hours_bn: '',
  form_title: '', form_title_bn: '', subject_options: [],
};

const ContactPageCMS: React.FC = () => {
  const [content, setContent] = useState<ContactContent>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/contact/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setContent(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContent(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubjectChange = (i: number, field: keyof SubjectOption, value: string) => {
    const s = [...content.subject_options];
    s[i] = { ...s[i], [field]: value };
    setContent(prev => ({ ...prev, subject_options: s }));
  };

  const addSubject = () => setContent(prev => ({
    ...prev, subject_options: [...prev.subject_options, { value: '', label: '', label_bn: '' }],
  }));

  const removeSubject = (i: number) => setContent(prev => ({
    ...prev, subject_options: prev.subject_options.filter((_, idx) => idx !== i),
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/cms/contact/1/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (res.ok) { setContent(await res.json()); Swal.fire({ icon: 'success', title: 'Saved!', text: 'Contact page updated', timer: 1500, showConfirmButton: false }); }
      else throw new Error();
    } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save' }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="homepage-cms-container"><div className="cms-card"><div className="card-content">
      <div className="cms-skeleton" style={{ height: 40 }} /><div className="cms-skeleton" style={{ height: 40, marginTop: 12 }} />
    </div></div></div>
  );

  return (
    <div className="homepage-cms-container">
      <form onSubmit={handleSubmit}>

        {/* Hero */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCDE'}</div><h3>Hero Section</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Hero Title (English)</label><input className="form-input" name="hero_title" value={content.hero_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Title (বল)</label><input className="form-input" name="hero_title_bn" value={content.hero_title_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-grid-2" style={{ marginTop: 8 }}>
              <div className="cms-form-group"><label>Hero Subtitle (English)</label><input className="form-input" name="hero_subtitle" value={content.hero_subtitle} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Hero Subtitle (বল)</label><input className="form-input" name="hero_subtitle_bn" value={content.hero_subtitle_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCCD'}</div><h3>Contact Information</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2" style={{ marginBottom: 12 }}>
              <div className="cms-form-group"><label>Location (English)</label><input className="form-input" name="location" value={content.location} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Location (বল)</label><input className="form-input" name="location_bn" value={content.location_bn} onChange={handleChange} /></div>
            </div>
            <div className="cms-form-group" style={{ marginBottom: 12 }}>
              <label>Email</label>
              <input className="form-input" name="email" type="email" value={content.email} onChange={handleChange} />
            </div>
            <div className="cms-grid-2">
              <div className="cms-form-group">
                <label>Business Hours (English)</label>
                <textarea className="form-input" name="business_hours" value={content.business_hours} onChange={handleChange} rows={3} style={{ resize: 'vertical' }} />
                <span className="field-hint">Use newlines to separate multiple lines</span>
              </div>
              <div className="cms-form-group">
                <label>Business Hours (বল)</label>
                <textarea className="form-input" name="business_hours_bn" value={content.business_hours_bn} onChange={handleChange} rows={3} style={{ resize: 'vertical' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Form Settings */}
        <div className="cms-card">
          <div className="card-header"><div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCDD'}</div><h3>Form Settings</h3></div></div>
          <div className="card-content">
            <div className="cms-grid-2">
              <div className="cms-form-group"><label>Form Title (English)</label><input className="form-input" name="form_title" value={content.form_title} onChange={handleChange} /></div>
              <div className="cms-form-group"><label>Form Title (বল)</label><input className="form-input" name="form_title_bn" value={content.form_title_bn} onChange={handleChange} /></div>
            </div>
          </div>
        </div>

        {/* Subject Options */}
        <div className="cms-card">
          <div className="card-header">
            <div className="card-header-left"><div className="card-icon icon-blue">{'\uD83D\uDCCB'}</div><h3>Subject Options</h3></div>
            <button type="button" className="btn-add-feature" onClick={addSubject}>+ Add Subject</button>
          </div>
          <div className="card-content">
            {content.subject_options.length === 0 && <div className="empty-state"><p>No subject options. Click "Add Subject" to start.</p></div>}
            {content.subject_options.map((s, i) => (
              <div key={i} className="feature-item-card" style={{ marginBottom: 10 }}>
                <div className="feature-header">
                  <span className="feature-count">#{i + 1}</span>
                  <button type="button" className="btn-remove-feature" onClick={() => removeSubject(i)}>{'\u2715'}</button>
                </div>
                <div className="cms-form-group" style={{ marginBottom: 8 }}>
                  <label>Value (slug)</label>
                  <input className="form-input" value={s.value} onChange={e => handleSubjectChange(i, 'value', e.target.value)} placeholder="e.g. order" style={{ maxWidth: 300 }} />
                </div>
                <div className="cms-grid-2">
                  <div className="cms-form-group"><label>Display Label (English)</label><input className="form-input" value={s.label} onChange={e => handleSubjectChange(i, 'label', e.target.value)} placeholder="e.g. Order Inquiry" /></div>
                  <div className="cms-form-group"><label>Display Label (বল)</label><input className="form-input" value={s.label_bn} onChange={e => handleSubjectChange(i, 'label_bn', e.target.value)} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cms-page-actions">
          <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : '\uD83D\uDCBE Save Changes'}</button>
        </div>
      </form>
    </div>
  );
};

export default ContactPageCMS;
